import styled, { css, DefaultTheme } from 'styled-components';
import { getSpacingToken, getToken, TextStyle } from '@optimus-web/common';
import { Badge } from '../Badge';
import { Image } from '../Image';
import { Stack } from '../Stack';
import { Typography } from '../typography';
import { AvatarSize, AvatarStyleProps, BadgeElementProps } from './types';

export const AvatarContainerElement = styled(Stack)`
    position: relative;
`;

const getSize = ({ size = 'large', theme }: { size?: AvatarSize; theme: DefaultTheme }) => {
    const AVATAR_SIZES: Record<AvatarSize, number> = {
        small: getToken('sizing400')({ theme }),
        medium: getToken('sizing500')({ theme }),
        large: getToken('sizing700')({ theme }),
        extraLarge: getToken('sizing1300')({ theme }),
    };

    return css`
        height: ${AVATAR_SIZES[size]}px;
        width: ${AVATAR_SIZES[size]}px;
    `;
};

export const getTitleTextStyle = (size: AvatarSize = 'large') => {
    const AVATAR_TEXT_STYLES: Record<AvatarSize, `${TextStyle}`> = {
        small: 'bodyExtraSmallStrong',
        medium: 'bodySmallStrong',
        large: 'bodyMediumStrong',
        extraLarge: 'bodyLargeStrong',
    };

    return AVATAR_TEXT_STYLES[size];
};

export const AvatarElement = styled.div<AvatarStyleProps>`
    background-color: ${getToken('backgroundAlertBasicSecondary')};
    color: ${getToken('textStaticSecondary')};
    overflow: hidden;
    border-radius: ${getToken('borderRadiusRound')};
    display: inline-flex;
    justify-content: center;
    align-items: center;
    box-sizing: border-box;
    mask-image: radial-gradient(white, black);

    ${getSize};

    ${(props) =>
        props.border &&
        css`
            border: ${getToken('borderWidth150')}px solid ${getToken('borderStaticPrimary')};
        `}
`;

export const AvatarImageElement = styled(Image)`
    height: 100%;
    width: 100%;
`;

export const TitleElement = styled.div`
    line-height: 0;
`;

export const NotificationElement = styled(Badge)<{ size: Extract<AvatarSize, 'medium' | 'large'> }>`
    position: absolute;
    right: ${({ size }) => getSpacingToken(size === 'medium' ? '0' : '25')}px;
    top: ${({ size }) => getSpacingToken(size === 'medium' ? '0' : '25')}px;
`;

export const AvatarBadgeElement = styled(Stack)<BadgeElementProps>`
    position: absolute;
    width: ${getToken('sizing200')}px;
    height: ${getToken('sizing200')}px;
    border-radius: ${getToken('borderRadiusRound')};
    bottom: 0;
    box-sizing: border-box;
    background-color: ${getToken('backgroundStaticFlat')};
    padding: ${getSpacingToken('25')}px;
    right: ${({ size }) => (size === 'medium' ? '-2px' : 0)};

    ${({ border }) =>
        border &&
        css`
            border: ${getToken('borderWidth150')}px solid ${getToken('borderStaticPrimary')};
        `}
`;

export const BadgeImageElement = styled(Image)`
    object-fit: contain;
    width: 100%;
    height: 100%;
    border-radius: ${getToken('borderRadiusRound')};
`;

export const BadgeTitleContainer = styled.div`
    text-align: center;

    ${Typography} {
        line-height: ${getToken('sizing200')}px;
    }
`;
